/**
 * СОПОСТАВЛЕНИЕ НАБРАННОГО С КАТАЛОГОМ — сердцевина правила владельца от 30.09.2026:
 *
 * > «нам надо менять железо. Вот если мы не понимаем что это такое тут надо показывать расхождение
 * > и давать какую то возможность описать гир»
 *
 * Отсюда две ветки, и различает их ТОЛЬКО эта функция: узнали уверенно — меняем железо у предмета
 * молча; не узнали — показываем расхождение и даём описать. Поэтому «уверенно» тут значит
 * «совпало», а не «похоже»: подменить микрофон на сцене по нечёткому совпадению хуже, чем не
 * подменить вовсе.
 *
 * Ни DOM, ни сети: каталог приходит готовым списком, и всё поведение проверяется тестами на живых
 * строках из корпуса (`docs/RESEARCH_GEAR_VOCABULARY.md`).
 */
import { CATALOG_BRANDS } from "./gear-brands.data";
/**
 * НОРМАЛИЗАЦИЯ — самое дешёвое и самое доходное, что можно сделать со словарём: по корпусу люди
 * пишут `sm 57`, `SM58`, `E904`, а в каталоге лежит `SM 57` и `Sennheiser e904`. Регистр и пробелы
 * не несут смысла, и их снятие даёт совпадения даром, без единого нового слова в синонимах.
 *
 * Дефисы и точки снимаются по той же причине: `KMS-105` и `KMS 105` — одна и та же вещь.
 */
export function normalizeGearName(value) {
    return value
        .toLowerCase()
        .replace(/[\s\-_.()]/g, "")
        .trim();
}
/**
 * СОКРАЩЕНИЯ МАРОК, замеченные в корпусе: `Sennh e904`, `Senn 614`. Марка снимается перед
 * сравнением, потому что человек её то пишет, то нет: `e904` встречается чаще, чем
 * `Sennheiser e904`, и обе записи означают один микрофон.
 *
 * Список короткий и намеренно неполный: сюда попадает только то, что реально встретилось. Гадать за
 * отрасль — не наше дело, полноту принесёт выборка из группы (SCRUM-441) и практик.
 */
export const MANUFACTURER_WORDS = [
    "shure",
    "sennheiser",
    "sennh",
    "senn",
    "akg",
    "neumann",
    "dpa",
    "audix",
    "beyerdynamic",
    "beyer",
    "rode",
    "audiotechnica",
    "electrovoice",
    "radial",
];
/**
 * Имя без марки: `sennheisere904` -> `e904`, `fendertwinreverb` -> `twinreverb`.
 *
 * Марки берутся ИЗ НАШЕЙ СОБСТВЕННОЙ таблицы производителей (`gear-brands.data.ts`, 68 штук) плюс
 * сокращения выше. До 06.10.2026 здесь стоял только ручной список из четырнадцати микрофонных
 * марок, и «Fender Twin Reverb» не сводился с лежащим в каталоге «Twin Reverb 65» — выглядело это
 * так, будто у нас нет бэклайна (SCRUM-606).
 *
 * Три условия, и каждое закрывает свою порчу:
 *  - берётся САМАЯ ДЛИННАЯ подходящая марка: `senn` и `sennheiser` обе в списке;
 *  - строка, которая ЦЕЛИКОМ является маркой, не трогается: иначе `radialengineering` стал бы
 *    `engineering`;
 *  - если после снятия ничего не остаётся, возвращается исходное.
 */
export function stripManufacturer(normalized) {
    if (ALL_BRANDS.has(normalized))
        return normalized;
    let best = "";
    for (const word of ALL_BRANDS) {
        if (word.length > best.length && normalized.startsWith(word) && normalized.length > word.length)
            best = word;
    }
    return best === "" ? normalized : normalized.slice(best.length);
}
/** Ручной список и каталожный вместе, по одному разу. */
const ALL_BRANDS = new Set([...MANUFACTURER_WORDS, ...CATALOG_BRANDS]);
/**
 * АЛЬТЕРНАТИВЫ В ОДНОМ ПОЛЕ. В корпусе это `SM81 / other`, `sm58 / sm58 Beta`, `SM81,С1000(s)` —
 * не грязь, а способ сказать «пойдёт то или то». Разделяем, чтобы каждую часть можно было узнать
 * отдельно; собирать обратно — дело того, кто показывает.
 *
 * Разделители взяты из корпуса: косая черта, запятая, слово «or». Пробел разделителем НЕ считается:
 * `Long microphone stand` — одно название, а не три.
 */
export function splitAlternatives(value) {
    return value
        .split(/\s*(?:\/|,|\bor\b)\s*/i)
        .map((part) => part.trim())
        .filter((part) => part.length > 0);
}
/**
 * Что каталог знает про набранную строку.
 *
 * `exact` — нормализованные имена совпали, с маркой или без. Только это даёт право поменять железо
 * молча.
 *
 * `likely` — одно является началом другого: `beta52` против `BETA 52A`, `kms105` против
 * `Neumann KMS-105`. Похоже — и почти наверняка то же самое, но «почти» здесь недостаточно:
 * является ли `Beta 52A` тем же, что `beta52`, решает звукорежиссёр, а не длина строки
 * (`docs/RESEARCH_GEAR_VOCABULARY.md` §6 — вопрос практику).
 */
/**
 * Имя, которым вещь МОЖНО УЗНАТЬ — то есть годное для правила «одно начинается с другого».
 *
 * Найдено на деве 11.10.2026: строка гира «Splash» (тарелка) ссылалась на `DiGiGrid S` — сетевой
 * интерфейс. У этой позиции после снятия марки остаётся имя из одной буквы, и такое имя оказывается
 * началом чего угодно: «Short Boom Stand», «Stereo Passive Direct Box», «Stage Box 16 In / 8 Out».
 * Цена ошибки несимметрична: пропущенная подсказка стоит щелчка, подставленное не то железо едет на
 * площадку.
 *
 * Две буквы с цифрой вещь называют («D6», «e6»), две буквы без цифры — нет. Точного совпадения это
 * не касается: кто написал «DiGiGrid S» целиком, тот её и получит.
 */
function identifying(bare) {
    return bare.length >= 3 || (bare.length === 2 && /\d/.test(bare));
}
export function matchGear(typed, catalog) {
    const wanted = normalizeGearName(typed);
    if (wanted === "")
        return { confidence: "none", candidates: [] };
    const wantedBare = stripManufacturer(wanted);
    // Какую марку человек написал перед моделью, если написал. Нужна не для сравнения, а для ОТКАЗА:
    // «Nexo M15» не должен совпасть с M15 другой марки (SCRUM-481). Снятая марка — это потерянное
    // сведение, и там, где маркa позиции известна, терять его нельзя.
    const wantedBrand = wanted.length > wantedBare.length ? wanted.slice(0, wanted.length - wantedBare.length) : "";
    const exact = [];
    const likely = [];
    for (const entry of catalog) {
        const name = normalizeGearName(entry.name);
        const bare = stripManufacturer(name);
        if (name === wanted) {
            exact.push(entry);
            continue;
        }
        if (wantedBrand !== "" && entry.manufacturer && normalizeBrand(entry.manufacturer) !== normalizeBrand(wantedBrand)) {
            continue; // человек назвал другую марку — это не этот предмет
        }
        if (bare === wantedBare) {
            exact.push(entry);
            continue;
        }
        if (identifying(bare) && identifying(wantedBare) && (bare.startsWith(wantedBare) || wantedBare.startsWith(bare)))
            likely.push(entry);
    }
    if (exact.length > 0)
        return { confidence: "exact", candidates: dedupe(exact) };
    if (likely.length > 0)
        return { confidence: "likely", candidates: dedupe(likely) };
    return { confidence: "none", candidates: [] };
}
/**
 * Подсказки по началу набора — то, что показывается в выпадающем списке, пока человек печатает.
 * Порядок: сперва то, что начинается с набранного, потом то, что его содержит; внутри — по длине
 * имени, чтобы короткое и точное шло раньше длинного и составного.
 */
export function suggestGear(typed, catalog, limit = 8) {
    const wanted = stripManufacturer(normalizeGearName(typed));
    if (wanted.length < 2)
        return [];
    const starts = [];
    const contains = [];
    for (const entry of catalog) {
        const bare = stripManufacturer(normalizeGearName(entry.name));
        if (bare.startsWith(wanted))
            starts.push(entry);
        else if (bare.includes(wanted))
            contains.push(entry);
    }
    const byLength = (a, b) => a.name.length - b.name.length;
    return dedupe([...starts.sort(byLength), ...contains.sort(byLength)]).slice(0, limit);
}
/**
 * WORDS A RENTAL INVENTORY ADDS AFTER THE MODEL to say what the item is: «SM58 Vocal Mic»,
 * «FR250z Subwoofer», «E604 Drum mic» (SCRUM-481, the Pro Sound & Lights inventory). They carry no
 * model information, so a model followed ONLY by these words is still that model. Anything else
 * after the model («SM58 Beta», «MAC Aura XB», «32 Channel») keeps the row at "check": the list is
 * an allow-list on purpose, and a word missing from it costs a click, never a wrong substitution.
 */
export const DESCRIPTOR_WORDS = new Set([
    "mic", "mics", "microphone", "vocal", "instrument", "drum", "kick", "snare", "choir", "podium", "stage",
    "subwoofer", "sub", "speaker", "speakers", "loudspeaker", "cabinet", "line", "array", "module",
    "monitor", "wedge", "compressor", "limiter", "crossover", "controller", "processor",
    "amplifier", "amp", "mixer", "console", "wireless", "system", "receiver", "transmitter",
    "active", "passive", "powered",
]);
/**
 * ОДИН ПРОИЗВОДИТЕЛЬ — НЕСКОЛЬКО НАПИСАНИЙ. Владелец 11.10.2026: «Нам нужно сделать таблицу или
 * механизм распознавания разных написаний производителей и моделей». Регистр, пробелы, дефисы и
 * `&`/`and` снимает `normalizeBrand` сама; сюда попадает только то, что переживает нормализацию.
 *
 * Ключ — написание, к которому сводим; значение — другие написания той же фирмы, уже
 * нормализованные. Каждая строка — с тем, где написание ВСТРЕЧЕНО. Правила «похоже — значит то же»
 * здесь нет и не будет (запрет 14): «Technika» и «Technica» сводит строка таблицы, а не близость букв.
 */
export const MANUFACTURER_SPELLINGS = {
    // Дев, производитель 18: «Audio-Technika» (GET /manufacturers, 11.10.2026); сама фирма и опись
    // Pro Sound & Lights пишут «Audio-Technica» / «Audio Technica». Из-за этого AE3000 и AE5100 описи
    // не узнавались, хотя в каталоге есть (SCRUM-484).
    audiotechnica: ["audiotechnika"],
    // Дев, производитель 16: «Telefunken»; фирма подписывается «TELEFUNKEN Elektroakustik» (SCRUM-568).
    telefunken: ["telefunkenelektroakustik"],
    // Дев, производитель 3: «d&b»; фирма — «d&b audiotechnik» (dbaudio.com).
    dandb: ["dandbaudiotechnik"],
    // Опись Pro Sound & Lights пишет «American DJ» — прежнее имя фирмы, которая сама теперь подписывается
    // «ADJ» (adj.com); в каталоге она ADJ (SCRUM-484).
    adj: ["americandj"],
    // Та же опись: «Marantz» · «PMD570»; рекордер выпускала Marantz Professional (D&M), так она и заведена.
    marantzprofessional: ["marantz"],
    // Coverage step 2 (28.10.2026), foreign riders of SCRUM-441. Each spelling below stands in the corpus
    // as the FIRST word of a model name and was read as "another maker" (SCRUM-481 refuses that on purpose,
    // so the fix is a row here, not a weaker rule). Sennheiser: «Senn e604», «Sennh e904», «Senh e904» are
    // the abbreviations; «Senheiser», «Sennhiser», «Seinnheiser», «Sennhtiser» are misspellings of the
    // whole name, each seen as typed. Garbled ones («Snne904», «Shire») are NOT listed: a table row says
    // what a word MEANS, and nobody can say that of those.
    sennheiser: ["senn", "sennh", "senh", "senheiser", "sennhiser", "seinnheiser", "sennhtiser"],
    // «EV RE20», «EV ND468»: Electro-Voice's own short name (the logo reads EV).
    electrovoice: ["ev"],
    // The firm is «Radial Engineering»; its logo and every rental list say «Radial» («DI RADIAL J48»).
    radialengineering: ["radial"],
    // «NEUMAN KM184»: one n short; Neumann is the firm's only spelling.
    neumann: ["neuman"],
};
const SPELLING_TO_MAKER = new Map(Object.entries(MANUFACTURER_SPELLINGS).flatMap(([maker, spellings]) => spellings.map((s) => [s, maker])));
/** `Allen &amp; Heath`, `Allen & Heath`, `allen and heath` are one maker; other spellings — `MANUFACTURER_SPELLINGS`. */
export function normalizeBrand(value) {
    const key = value
        .replace(/&amp;/gi, "&")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]/g, "");
    return SPELLING_TO_MAKER.get(key) ?? key;
}
/** Bracketed notes (`(x16)` = units in the set), `w/ …` extras and `– …` remarks are not part of the model. */
function cleanInventoryModel(model) {
    return model
        .replace(/\([^)]*\)/g, " ")
        .replace(/\s+w\/.*$/i, "")
        .replace(/\s+[–—]\s+.*$/, "")
        .trim();
}
/**
 * A row of a company's inventory: model + manufacturer as separate columns (SCRUM-481). The
 * manufacturer narrows the search to that maker's items first — «Shure» · «SM58 Vocal Mic» is then
 * compared with Shure's catalog, where `SM 58` is found by its leading words. Without a known maker
 * it falls back to `matchGear` over the whole catalog, exactly as before.
 *
 * `exact` only when the leading words equal a catalog model AND everything after them is a
 * `DESCRIPTOR_WORDS` word; a different tail, or a catalog model that merely starts the same way
 * (`E602` vs `e602-II`), is `likely` — offered, never substituted.
 */
export function matchInventoryItem(model, manufacturer, catalog) {
    const brand = normalizeBrand(manufacturer);
    const pool = brand === "" ? [] : catalog.filter((e) => e.manufacturer !== undefined && normalizeBrand(e.manufacturer) === brand);
    if (pool.length === 0) {
        const withBrand = brand === "" || normalizeBrand(model).startsWith(brand) ? model : `${manufacturer} ${model}`;
        return { ...matchGear(withBrand, catalog), manufacturerKnown: false };
    }
    const bare = (entry) => {
        const name = normalizeGearName(entry.name);
        return name.startsWith(brand) && name.length > brand.length ? name.slice(brand.length) : name;
    };
    const tokens = cleanInventoryModel(model)
        .split(/[\s/]+/)
        .filter((t) => t !== "");
    for (let k = tokens.length; k >= 1; k--) {
        const head = normalizeGearName(tokens.slice(0, k).join(""));
        const descriptive = tokens.slice(k).every((t) => DESCRIPTOR_WORDS.has(t.toLowerCase().replace(/[^a-z]/g, "")));
        const hits = pool.filter((entry) => bare(entry) === head);
        if (hits.length > 0) {
            return { confidence: descriptive ? "exact" : "likely", candidates: dedupe(hits), manufacturerKnown: true };
        }
        const aliased = pool.filter((entry) => modelAliases(entry.name).has(head));
        if (aliased.length === 0)
            continue;
        // «Shure 87A» is «BETA 87A» by its words (the line name «BETA» is dropped) AND «SM87A» by its series letter:
        // when a series reading finds an item the word reading did not, the string is ambiguous, not exact.
        const series = restoreDroppedPrefix(brand, tokens.slice(0, k), pool, bare);
        const ambiguous = series.some((s) => !aliased.includes(s));
        const sure = descriptive && aliased.length === 1 && !ambiguous;
        return { confidence: sure ? "exact" : "likely", candidates: dedupe(ambiguous ? [...aliased, ...series] : aliased), manufacturerKnown: true };
    }
    const first = normalizeGearName(tokens[0] ?? "");
    const near = first.length < 3
        ? []
        : pool.filter((entry) => {
            const b = bare(entry);
            return b.startsWith(first) || (b.length >= 3 && first.startsWith(b));
        });
    if (near.length > 0)
        return { confidence: "likely", candidates: dedupe(near), manufacturerKnown: true };
    const restored = restoreDroppedPrefix(brand, tokens, pool, bare);
    if (restored.length > 0)
        return { confidence: "likely", candidates: dedupe(restored), manufacturerKnown: true };
    return { confidence: "none", candidates: [], manufacturerKnown: true };
}
/**
 * THE SERIES LETTER PEOPLE DROP (coverage step 2, 28.10.2026). A maker's model names start with a
 * series mark, and a rider says the number: AKG C414 / D112 are «AKG 414» / «AKG 112», Sennheiser
 * e604 / MD 421 are «Senn 604» / «Sennheiser 421», Shure SM58 / Beta 58A are «Shure 58». One row per
 * maker, and ONLY a maker whose naming is like that AND which the foreign riders write this way; a maker
 * without a row (Neumann, DPA) is read as before.
 *
 * The result is `likely`, never `exact`, however many items answer: the letter that was dropped is what
 * tells «C414 XLII» from «C414 XLS» and «SM58» from «Beta 58A», and choosing between them is the human's
 * (запрет 14: чинится чтение, а не придумывается замена).
 */
export const MODEL_SERIES_PREFIXES = {
    akg: ["c", "d"],
    sennheiser: ["e", "md"],
    shure: ["sm", "beta"],
};
/** Shorthand for a series word, per maker: Shure's «B57» / «B 58» is its «Beta 57A» / «Beta 58A». */
export const MODEL_SERIES_SHORTHAND = {
    shure: { b: "beta" },
};
function restoreDroppedPrefix(brand, tokens, pool, bare) {
    const prefixes = MODEL_SERIES_PREFIXES[brand] ?? [];
    const shorthand = MODEL_SERIES_SHORTHAND[brand] ?? {};
    for (let k = tokens.length; k >= 1; k--) {
        const head = normalizeGearName(tokens.slice(0, k).join(""));
        const wanted = [];
        if (/^\d{2,4}[a-z]{0,3}$/.test(head))
            for (const p of prefixes)
                wanted.push(p + head);
        const m = /^([a-z]+)(\d{2,4}[a-z]{0,3})$/.exec(head);
        const word = m?.[1] === undefined ? undefined : shorthand[m[1]];
        if (m !== null && word !== undefined)
            wanted.push(word + m[2]);
        if (wanted.length === 0)
            continue;
        // What is typed AFTER the head. A short tail («E», «EB», «XL») is a model suffix and must agree with the
        // item's own: «AKG 451 E» / «451 EB» are the older E / EB capsules, not «C451 B». Descriptive words
        // («Drum Mic») and anything longer than a suffix («FROND») say nothing about the model.
        const tail = normalizeGearName(tokens
            .slice(k)
            .filter((t) => !DESCRIPTOR_WORDS.has(t.toLowerCase().replace(/[^a-z]/g, "")))
            .join(""));
        const checkable = /^(?:[a-z]{1,3}|[ivxl]{1,4})$/.test(tail);
        // «AKG 41» must not reach «C414»: a typed number that ends in a digit has to end where the item's does.
        const hits = pool.filter((entry) => {
            const b = bare(entry);
            return wanted.some((w) => b.startsWith(w) && !(/\d$/.test(w) && /\d/.test(b.charAt(w.length))) && (!checkable || b.slice(w.length).startsWith(tail)));
        });
        if (hits.length > 0)
            return hits;
    }
    return [];
}
/**
 * A name typed WITHOUT a separate maker column — «Nexo PS8», «Outline Mantas 28» — while the catalog
 * keeps maker and model apart (NEXO · «PS8»). `matchGear` alone cannot see it: it only knows the
 * short `MANUFACTURER_WORDS` list. So when the plain match is not `exact`, the leading words are
 * tried as a catalog maker (case, `&`, hyphens ignored) and the rest is matched inside that maker's
 * items by `matchInventoryItem`. The split result wins only when it is strictly more confident, so
 * a model the maker does not have still stays `none`/`likely`, never a wrong item.
 */
export function matchTypedName(typed, catalog) {
    const makers = new Map();
    for (const entry of catalog)
        if (entry.manufacturer)
            makers.set(normalizeBrand(entry.manufacturer), entry.manufacturer);
    const rank = { none: 0, likely: 1, exact: 2 };
    let best = matchTypedNameAs(typed, catalog, makers);
    if (best.confidence === "exact")
        return best;
    // Other ways to READ the same string (coverage step 2); the first reading stays unless another is strictly
    // surer. A reading that drops or adds words may answer ONLY with its own maker's items: «PZM 160» minus
    // «PZM» is the bare number 160, and a bare number is the start of somebody else's «1604» (reviewed 28.10.2026).
    for (const reading of otherReadings(typed, makers)) {
        const alt = matchTypedNameAs(reading.text, catalog, makers);
        if (rank[alt.confidence] <= rank[best.confidence])
            continue;
        if (!alt.candidates.every((c) => c.manufacturer !== undefined && normalizeBrand(c.manufacturer) === reading.maker))
            continue;
        best = alt;
    }
    return best;
}
/**
 * DPA's product LINES are written in front of the model number and are not part of it: «d:vote 4099»,
 * «d:fine 4066» (dpamicrophones.com). The catalogue keeps the line inside long names, so this reading
 * is only a second try after the string as typed. The colon may already be a space («d vote»): the
 * coverage tool's `cleanPart` turns every colon into one.
 */
const DPA_LINE = /\bd[:\s]\s*(?:vote|fine|screet|dicate|facto|mension|sign)\s+/i;
/**
 * «PZM pcc-160»: PZM (Pressure Zone Microphone) is Crown's name for the boundary-microphone family and
 * stands in front of models that are called PCC-160, PCC-170 by the firm. Tried second, so that a name
 * which really holds it («PZM-30D») is still read whole.
 */
const PZM_FAMILY = /^pzm[\s-]+(?=.*\d)/i;
/**
 * A bare «B57» / «B 58» names no maker, and «B» is Shure's Beta in every rental list. The reading is
 * only tried when the string as typed found nothing better, and the answer is `likely` at most
 * (see `MODEL_SERIES_SHORTHAND`); a catalogue without Shure's Beta items answers `none` as before.
 */
const BARE_BETA = /^b[\s-]?(\d{2,3}[a-z]?)$/i;
function otherReadings(typed, makers) {
    const text = typed.trim();
    const out = [];
    if (DPA_LINE.test(text))
        out.push({ text: text.replace(DPA_LINE, ""), maker: normalizeBrand("DPA") });
    if (PZM_FAMILY.test(text))
        out.push({ text: text.replace(PZM_FAMILY, ""), maker: normalizeBrand("Crown") });
    // «Akg451», «Senn421», «Shure58»: a maker's name glued to the model. Only a real maker of THIS
    // catalogue is cut off, and only when what follows starts with a digit: «Beta58» and «RE20» stay whole.
    const glued = /^([A-Za-z]+)(\d.*)$/.exec(text);
    if (glued !== null && makers.has(normalizeBrand(glued[1] ?? "")))
        out.push({ text: `${glued[1]} ${glued[2]}`, maker: normalizeBrand(glued[1] ?? "") });
    const bare = BARE_BETA.exec(text);
    if (bare !== null && makers.has("shure"))
        out.push({ text: `Shure B${bare[1]}`, maker: "shure" });
    return out;
}
function matchTypedNameAs(typed, catalog, makers) {
    const plain = { ...matchGear(typed, catalog), manufacturerKnown: false };
    if (plain.confidence === "exact")
        return plain;
    const words = typed.trim().split(/\s+/);
    const rank = { none: 0, likely: 1, exact: 2 };
    for (let k = Math.min(3, words.length - 1); k >= 1; k--) {
        const maker = makers.get(normalizeBrand(words.slice(0, k).join(" ")));
        if (maker === undefined)
            continue;
        const split = matchInventoryItem(words.slice(k).join(" "), maker, catalog);
        return rank[split.confidence] > rank[plain.confidence] ? split : plain;
    }
    return plain;
}
/**
 * Other names a catalog model answers to (SCRUM-533). Console makers put a PRODUCT LINE in front of
 * the model and the system's part number in brackets — «VENUE S6L-32D», «RIVAGE PM10 (CS-R10)» — and
 * a rental list writes «S6L-32D», «Rivage PM10», «PM10» or «CS-R10». So the name without its
 * brackets, the bracket contents, and each of these with leading WHOLE words dropped all count.
 * Only keys with a digit and at least three characters: «PM» or «Sub» alone names a family, not an
 * item. Used only after the full name failed, and substitutes only when exactly one item answers.
 */
function modelAliases(name) {
    const variants = [name.replace(/\([^)]*\)/g, " "), ...[...name.matchAll(/\(([^)]*)\)/g)].map((m) => m[1] ?? "")];
    const keys = new Set();
    for (const variant of variants) {
        const words = variant.trim().split(/\s+/).filter((w) => w !== "");
        for (let i = 0; i < words.length; i++) {
            const key = normalizeGearName(words.slice(i).join(""));
            if (key.length >= 3 && /\d/.test(key)) {
                keys.add(key);
                // Electro-Voice prints «N/D468», people type «ND468» / «ND 468»: the slash is punctuation of the name.
                if (key.includes("/"))
                    keys.add(key.replace(/\//g, ""));
            }
        }
    }
    return keys;
}
/** В каталоге есть дубли одной вещи («BETA 52A» и «Shure Beta 52A»), и показывать их дважды незачем. */
function dedupe(entries) {
    const seen = new Set();
    const out = [];
    for (const entry of entries) {
        const key = stripManufacturer(normalizeGearName(entry.name));
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(entry);
    }
    return out;
}
//# sourceMappingURL=gear-match.js.map